/** Every app router's search format: plain text, `?week=3,4`. TanStack's
 *  default is JSON, which wrote `?week=3` as `?week=%223%22` (#782) and read a
 *  numeric id back as a number. Here every value reads back as the string it
 *  was: each route's `validateSearch` takes strings (`validateListSearch` and
 *  friends), and nothing is ever coerced. A list is written as a repeated key
 *  and read back as an array, which `validateListSearch` joins. A link written
 *  in the old JSON form (`%223%22`) still reads as `3`. Pass both to
 *  `createRouter`.
 *
 *  Its own entry point (`@region13/ui/searchFormat`), outside the barrel, so it
 *  stays out of every test that mocks the router whole. */

type SearchValue = string | string[]

/** A value the old JSON format quoted (`"3"`), unquoted; anything else as is. */
function unquote(value: string): string {
  if (value.length < 2 || !value.startsWith('"') || !value.endsWith('"')) return value
  try {
    const parsed: unknown = JSON.parse(value)
    return typeof parsed === 'string' ? parsed : value
  } catch {
    return value
  }
}

export function parseSearch(searchStr: string): Record<string, SearchValue> {
  const params = new URLSearchParams(searchStr.startsWith('?') ? searchStr.slice(1) : searchStr)
  const out: Record<string, SearchValue> = {}
  for (const key of new Set(params.keys())) {
    const values = params.getAll(key).map(unquote)
    out[key] = values.length === 1 ? values[0]! : values
  }
  return out
}

export function stringifySearch(search: Record<string, unknown>): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(search)) {
    for (const v of Array.isArray(value) ? value : [value]) {
      if (v === undefined || v === null) continue
      params.append(key, typeof v === 'object' ? JSON.stringify(v) : String(v))
    }
  }
  const s = params.toString()
  return s ? `?${s}` : ''
}
